import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MessagesSquare, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import Layout from "../components/Layout";
import ProjectCard from "../components/ProjectCard";
import LockModal from "../components/LockModal";

export default function Home() {
  const { t } = useI18n();
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [discordUrl, setDiscordUrl] = useState("#");
  const [googleFormUrl, setGoogleFormUrl] = useState("#");
  const [lockOpen, setLockOpen] = useState(false);
  const [showNext, setShowNext] = useState(false);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/config").then((r) => {
      setDiscordUrl(r.data.discord_url);
      setGoogleFormUrl(r.data.google_form_url || "#");
    }).catch(() => {});
  }, []);

  const openProject = (p) => {
    if (isLoggedIn) navigate(`/projects/${p.id}`);
    else setLockOpen(true);
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 nh-grid-bg opacity-60" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
          <span className="font-mono text-xs tracking-[0.3em] uppercase text-nhgreen nh-fade-up">
            {t("hero_tag")}
          </span>
          <h1 className="font-mono font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4 nh-fade-up" style={{ animationDelay: "80ms" }}>
            NAŠA <span className="text-nhgreen text-glow-green">HUB</span>
          </h1>
          <p className="text-white/60 text-base sm:text-lg leading-relaxed max-w-2xl mt-6 nh-fade-up" style={{ animationDelay: "160ms" }}>
            {t("hero_sub")}
          </p>
          {!isLoggedIn && (
            <div className="flex flex-col sm:flex-row gap-3 mt-8 nh-fade-up" style={{ animationDelay: "240ms" }}>
              <button
                data-testid="hero-register-btn"
                onClick={() => navigate("/register")}
                className="font-mono font-bold text-sm bg-nhgreen text-black rounded-md px-6 py-3 hover:bg-[#1ee068] hover:shadow-[0_0_15px_rgba(34,255,119,0.4)] transition-[background-color,box-shadow] duration-200 flex items-center justify-center gap-2"
              >
                {t("nav_register")} <ArrowRight className="w-4 h-4" />
              </button>
              <button
                data-testid="hero-login-btn"
                onClick={() => navigate("/login")}
                className="font-mono font-bold text-sm bg-transparent border border-white/20 text-white rounded-md px-6 py-3 hover:border-nhgreen hover:text-nhgreen transition-colors duration-200"
              >
                {t("nav_login")}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Projects */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16" id="projects">
        <div className="mb-10">
          <h2 className="font-mono font-bold text-2xl sm:text-3xl">{t("projects_title")}</h2>
          <p className="text-white/50 text-sm mt-3 max-w-2xl">{t("projects_sub")}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
          {projects.filter((p) => !p.future).map((p, i) => (
            <ProjectCard key={p.id} project={p} onOpen={openProject} index={i} />
          ))}
          {projects.some((p) => p.future) && (
            <div
              data-testid="next-projects-card"
              onClick={() => setShowNext((v) => !v)}
              className="group cursor-pointer bg-[#0A0A0A] border border-dashed border-nhorange/40 rounded-2xl p-6 flex flex-col items-center justify-center min-h-[300px] transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-1 hover:border-nhorange hover:shadow-[0_0_24px_rgba(255,122,24,0.18)]"
            >
              <span className="font-mono text-5xl text-nhorange/60 group-hover:text-nhorange transition-colors duration-200">
                {showNext ? "\u2212" : "+"}
              </span>
              <h3 className="font-mono font-bold text-lg text-white mt-4 text-center">
                BUDUĆI PROJEKTI
              </h3>
              <p className="text-sm text-white/50 text-center mt-2">
                {showNext ? "Sakrij" : "Pogledaj šta sledi"}
              </p>
            </div>
          )}
        </div>

        {showNext && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6" data-testid="next-projects-grid">
            {projects.filter((p) => p.future).map((p, i) => (
              <ProjectCard key={p.id} project={p} onOpen={openProject} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* Discord */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-6">
        <a
          href={discordUrl}
          data-testid="discord-cta"
          className="group block bg-[#0A0A0A] border border-white/10 rounded-2xl p-8 transition-[border-color,box-shadow] duration-200 hover:border-nhgreen/50 hover:shadow-[0_0_24px_rgba(34,255,119,0.18)]"
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <div className="w-14 h-14 rounded-xl bg-nhgreen/10 border border-nhgreen/30 flex items-center justify-center shrink-0">
              <MessagesSquare className="w-7 h-7 text-nhgreen" />
            </div>
            <div className="flex-1">
              <h3 className="font-mono font-bold text-xl group-hover:text-nhgreen transition-colors duration-200">
                {t("discord_title")}
              </h3>
              <p className="text-white/50 text-sm mt-2">{t("discord_sub")}</p>
            </div>
            <span className="font-mono font-bold text-sm bg-nhgreen text-black rounded-md px-5 py-3 group-hover:shadow-[0_0_15px_rgba(34,255,119,0.4)] transition-shadow duration-200 whitespace-nowrap">
              {t("discord_btn")}
            </span>
          </div>
        </a>
        <a href={t("discord_guide_url")} data-testid="discord-guide" className="group inline-flex items-center gap-3 mt-4 font-mono font-bold text-sm text-nhgreen border border-nhgreen/40 bg-nhgreen/5 rounded-md px-5 py-3 hover:bg-nhgreen/10 hover:border-nhgreen hover:shadow-[0_0_18px_rgba(34,255,119,0.35)] transition-[background-color,border-color,box-shadow] duration-200">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-nhgreen opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-nhgreen" />
          </span>
          <span className="underline underline-offset-4 decoration-nhgreen/50">{t("discord_guide")}</span>
          <span className="transition-transform duration-200 group-hover:translate-x-1">→</span>
        </a>
      </section>

      {/* Propose a project CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16" id="propose">
        <div
          data-testid="propose-section"
          className="relative overflow-hidden bg-[#0A0A0A] border border-white/10 rounded-2xl p-8 sm:p-12"
        >
          <div className="absolute inset-0 nh-grid-bg opacity-40" />
          <div className="relative max-w-2xl">
            <span className="font-mono text-xs tracking-[0.3em] uppercase text-nhorange">
              NAŠA ZAJEDNICA
            </span>
            <h2 className="font-mono font-bold text-2xl sm:text-3xl mt-4 leading-tight">
              {t("propose_title")}
            </h2>
            <p className="text-white/60 text-sm sm:text-base leading-relaxed mt-4">
              {t("propose_sub")}
            </p>
            <a
              href={googleFormUrl}
              data-testid="propose-cta"
              className="inline-flex items-center gap-2 font-mono font-bold text-sm bg-nhgreen text-black rounded-md px-6 py-3 mt-7 hover:bg-[#1ee068] hover:shadow-[0_0_15px_rgba(34,255,119,0.4)] transition-[background-color,box-shadow] duration-200"
            >
              {t("propose_btn")} <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <LockModal open={lockOpen} onClose={() => setLockOpen(false)} />
    </Layout>
  );
}
